import { Button, Skeleton, ThemedView, Typography } from '@/components/atoms';
import {
    CircleQuestionMarkIcon,
    CircleUserRoundIcon,
    CopyIcon,
    HeadsetIcon,
    IdCardIcon,
    IdCardLanyardIcon,
    LandmarkIcon,
    LogOutIcon,
    ShieldUserIcon,
} from '@/components/atoms/icons';
import { ConfirmationDialog, OkDialog, Toast } from '@/components/molecules';
import { TextHeader } from '@/components/molecules/header/TextHeader';
import { useAuth } from '@/core/contexts/AuthContext';
import { useIsAdhaJourney, useSingleTapAction, useUIColors } from '@/core/hooks';
import { useTranslation } from '@/core/hooks/useTranslation';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import * as Clipboard from 'expo-clipboard';
import { router } from 'expo-router';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import React, { useEffect, useState } from 'react';
import { ScrollView, TouchableOpacity, View, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LanguageSwitch } from './LanguageSwitch';
import { ProfileRow } from './ProfileRow';
// @ts-ignore Dynatrace service is ts-nocheck; runtime export is available
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { useDeleteAccount } from '@/modules/auth/hooks/useDeleteAccount';

/** Shortest time the language-change shimmer stays up, so a fast switch doesn't just flash. */
const LANGUAGE_SHIMMER_MIN_MS = 500;
const PROFILE_ROW_SKELETON_KEYS = ['row-1', 'row-2', 'row-3', 'row-4'] as const;

/** Name + customer-ID placeholder while the language is switching. */
function ProfileNameSkeleton() {
    return (
        <View style={{ marginTop: 8, alignItems: 'center', gap: 8 }}>
            <Skeleton width={180} height={24} borderRadius={6} />
            <Skeleton width={140} height={16} borderRadius={6} />
        </View>
    );
}

/** Profile rows + logout placeholder while the language is switching. */
function ProfileRowsSkeleton() {
    return (
        <View style={{ marginTop: 12, paddingHorizontal: 16, gap: 12 }}>
            {PROFILE_ROW_SKELETON_KEYS.map((key) => (
                <Skeleton key={key} height={72} borderRadius={12} />
            ))}
            <Skeleton height={44} borderRadius={8} style={{ marginTop: 4 }} />
        </View>
    );
}

export function ProfileScreen() {
    const isAdhaJourney = useIsAdhaJourney();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { currentLanguage, changeLanguage, t } = useTranslation();
    const { logout } = useAuth(); // Use AuthContext
    const user = useAppStore((state) => state.auth.user);
    const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);
    const [deleteAccountDialogVisible, setDeleteAccountDialogVisible] = useState(false);
    const { deleteAccount, isLoading: deleteLoading, error: deleteError } = useDeleteAccount();
    const [deleteErrorDialogVisible, setDeleteErrorDialogVisible] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isChangingLanguage, setIsChangingLanguage] = useState(false);
    // Rapid repeat taps on a row would otherwise push the same screen several times.
    const runSingleTap = useSingleTapAction();

    // Shimmer from the toggle until changeLanguage resolves (native awaits the
    // translation preload), held for at least LANGUAGE_SHIMMER_MIN_MS. Repeat
    // taps while a change is in flight are ignored.
    const handleLanguageChange = async (language: 'en' | 'ar') => {
        if (language === currentLanguage || isChangingLanguage) {
            return;
        }
        setIsChangingLanguage(true);
        const startedAt = Date.now();
        try {
            await changeLanguage(language);
        } catch (error) {
            logger.error('ProfileScreen:Failed to change language', error);
        } finally {
            const remaining = LANGUAGE_SHIMMER_MIN_MS - (Date.now() - startedAt);
            if (remaining > 0) {
                await new Promise((resolve) => setTimeout(resolve, remaining));
            }
            setIsChangingLanguage(false);
        }
    };

    // Get display name from user data (prioritize full_name, then firstnameEN/lastnameEN, then name)
    // Ensure it's always a valid string (not null, undefined, or empty) to prevent React Native Text rendering errors
    const displayName = (() => {
        // Priority 1: full_name
        if (user?.full_name && typeof user.full_name === 'string' && user.full_name.trim()) {
            return user.full_name.trim();
        }
        // Priority 2: firstnameEN + lastnameEN
        const firstName = (user?.firstnameEN && typeof user.firstnameEN === 'string') ? user.firstnameEN.trim() : '';
        const lastName = (user?.lastnameEN && typeof user.lastnameEN === 'string') ? user.lastnameEN.trim() : '';
        const fullName = `${firstName} ${lastName}`.trim();
        if (fullName) {
            return fullName;
        }
        // Priority 3: name
        if (user?.name && typeof user.name === 'string' && user.name.trim()) {
            return user.name.trim();
        }
        // Fallback: translation key (always returns a string)
        return t('profile.myProfile') || 'My Profile';
    })();

    const adhaProjectDetails = useAppStore((state) => state.adhaProjectDetails);
    // ADHA journey shows the ADHA application number (GET /v1/customers/profile
    // `details.adha_application_number`), everyone else shows customer_number.
    const customerId = isAdhaJourney
        ? (adhaProjectDetails?.adha_application_number || null)
        : (user?.customer_number || null);

    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'profile',
            p_actionName: 'Profile Screen Viewed',
            eventType: 'view',
        });
    }, []);

    const handleCopyCustomerId = async () => {
        if (!customerId) {
            logger.warn('ProfileScreen:No customerId to copy');
            return;
        }

        try {
            await Clipboard.setStringAsync(String(customerId));
            logger.info('ProfileScreen:CustomerId copied to clipboard', { customerId });
            trackDynatraceEvent({
                p_screen: 'profile',
                p_actionName: 'Copy Customer ID',
                eventType: 'action',
            });
        } catch (error) {
            logger.error('ProfileScreen:Failed to copy customerId', error);
            trackDynatraceEvent({
                p_screen: 'profile',
                p_actionName: 'Copy Customer ID',
                p_failedMessage: error instanceof Error ? error.message : 'Failed to copy customer ID',
                eventType: 'error',
            });
        }
    };

    const handleDeleteAccountPress = () => {
        trackDynatraceEvent({
            p_screen: 'profile',
            p_actionName: 'Delete Account Button Pressed',
            eventType: 'action',
        });
        setDeleteAccountDialogVisible(true);
    };

    const handleDeleteAccountConfirm = async () => {
        setDeleteAccountDialogVisible(false);

        const success = await deleteAccount();

        if (!success) {
            // Show error dialog only on failure
            // Success case is handled by useDeleteAccount (logout + redirect to welcome)
            setDeleteErrorDialogVisible(true);
        }
    };

    return (
        <ThemedView style={{ flex: 1, backgroundColor: colors.backgroundLevel1 }}>
            <TextHeader title={t('profile.myProfile')} />
            <ScrollView contentContainerStyle={{ paddingBottom: Math.max(insets.bottom, 20) }}>

                {/* Avatar */}
                <View style={{ alignItems: 'center', marginTop: 48 }}>
                    <CircleUserRoundIcon size={102} color={colors.fontPrimary} />
                </View>

                {/* Name — shimmer while the language is switching */}
                {isChangingLanguage ? (
                    <ProfileNameSkeleton />
                ) : (
                <View style={{ marginTop: 4, alignItems: 'center', paddingHorizontal: 16 }}>
                    <Typography variant="title2" color="primary" style={{ textAlign: 'center' }}>
                        {displayName}
                    </Typography>
                    {customerId && (
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: 8,
                                columnGap: 8,
                            }}
                        >
                            <Typography variant="footnote" color="tertiary">
                                {isAdhaJourney ? t('profile.adhaApplication') : t('profile.customerId')}
                            </Typography>
                            <Typography variant="subhead" color="primary" numberOfLines={1}>
                                {String(customerId)}
                            </Typography>
                            <TouchableOpacity
                                onPress={handleCopyCustomerId}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                style={{ marginLeft: 4 }}
                            >
                                <CopyIcon size={18} color={colors.fontPrimary} />
                            </TouchableOpacity>
                        </View>
                    )}
                </View>
                )}

                {/* Language switch — always mounted (outside the shimmer) so its
                    slide animation plays while the rest of the screen loads */}
                <View style={{ marginTop: 24, paddingHorizontal: 16 }}>
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <View style={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="headline" color="secondary" numberOfLines={1}>
                                {t('profile.appLanguage')}
                            </Typography>
                        </View>
                        <View style={{ flexShrink: 0 }}>
                            <LanguageSwitch value={currentLanguage} onChange={handleLanguageChange} />
                        </View>
                    </View>
                </View>

                {/* Rows + logout — shimmer while the language is switching */}
                {isChangingLanguage ? (
                    <ProfileRowsSkeleton />
                ) : (
                <>
                <View style={{ marginTop: 12, paddingHorizontal: 16, gap: 12 }}>
                    <ProfileRow
                        leading={<IdCardLanyardIcon size={24} color={colors.fontPrimary} />}
                        title={t('profile.personalDetails')}
                        subtitle={t('profile.phoneNumberEmail')}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'profile',
                                p_actionName: 'Navigate to Personal Details',
                                eventType: 'action',
                            });
                            runSingleTap(() => router.push({ pathname: '/personal-details' } as any));
                        }}
                    />

                    {isAdhaJourney ? (
                        /* Loan Details row - ADHA Journey only */
                        <ProfileRow
                            leading={<LandmarkIcon size={24} color={colors.fontPrimary} />}
                            title={t('profile.loanDetails')}
                            subtitle={t('profile.loanDetailsSubtitle')}
                            onPress={() => {
                                trackDynatraceEvent({
                                    p_screen: 'profile',
                                    p_actionName: 'Navigate to Loan Details',
                                    eventType: 'action',
                                });
                                runSingleTap(() => router.push({ pathname: '/loan-details' } as any));
                            }}
                        />
                    ) : (
                        /* ID Proofs & Privacy Security rows - Non-ADHA (Email/Password) Journey */
                        <>
                            <ProfileRow
                                leading={<IdCardIcon size={24} color={colors.fontPrimary} strokeWidth={3.5} />}
                                title={t('profile.idProofs')}
                                subtitle={t('profile.emiratesIdAndPassport')}
                                onPress={() => {
                                    trackDynatraceEvent({
                                        p_screen: 'profile',
                                        p_actionName: 'Navigate to ID Proofs',
                                        eventType: 'action',
                                    });
                                    runSingleTap(() => router.push({ pathname: '/id-proof' } as any));
                                }}
                            />
                            <ProfileRow
                                leading={<ShieldUserIcon size={24} color={colors.fontPrimary} />}
                                title={t('profile.privacyTitle')}
                                subtitle={t('profile.passcodePassword')}
                                onPress={() => {
                                    trackDynatraceEvent({
                                        p_screen: 'profile',
                                        p_actionName: 'Navigate to Privacy Security',
                                        eventType: 'action',
                                    });
                                    runSingleTap(() => router.push({ pathname: '/privacy-security' } as any));
                                }}
                            />
                        </>
                    )}

                    <ProfileRow
                        leading={<CircleQuestionMarkIcon size={24} color={colors.fontPrimary} />}
                        title={t('profile.faq')}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'profile',
                                p_actionName: 'Navigate to FAQ',
                                eventType: 'action',
                            });
                            runSingleTap(() => router.push({ pathname: '/faq' } as any));
                        }}
                    />
                    <ProfileRow
                        leading={<HeadsetIcon size={24} color={colors.fontPrimary} />}
                        title={t('profile.customerSupport')}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'profile',
                                p_actionName: 'Navigate to Customer Support',
                                eventType: 'action',
                            });
                            runSingleTap(() => router.push({ pathname: '/service-request' } as any));
                        }}
                    />
                </View>

                {/* Logout */}
                <View style={{ marginTop: 16, paddingHorizontal: 16 }}>
                    <Button
                        title={t('profile.logOut')}
                        variant="text"
                        fullWidth={true}
                        leading={<LogOutIcon size={18} />}
                        onPress={() => {
                            trackDynatraceEvent({
                                p_screen: 'profile',
                                p_actionName: 'Logout Button Pressed',
                                eventType: 'action',
                            });
                            setLogoutDialogVisible(true);
                        }}
                    />
                </View>
                </>
                )}
            </ScrollView>

            <ConfirmationDialog
                visible={deleteAccountDialogVisible}
                message={t('profile.deactivateConfirmationTitle' as any)}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={handleDeleteAccountConfirm}
                onCancel={() => setDeleteAccountDialogVisible(false)}
            />

            <OkDialog
                visible={deleteErrorDialogVisible}
                message={deleteError || t('common.error')}
                onOk={() => {
                    setDeleteErrorDialogVisible(false);
                }}
            />

            <ConfirmationDialog
                visible={logoutDialogVisible}
                message={t('profile.logoutConfirmation')}
                confirmText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={async () => {
                    setLogoutDialogVisible(false);
                    trackDynatraceEvent({
                        p_screen: 'profile',
                        p_actionName: 'Logout Confirmed',
                        eventType: 'action',
                    });
                    try {
                        await logout();
                        if (Platform.OS === 'web') {
                            void navigateToWelcomeAfterLogout({ showToast: true });
                        } else {
                            router.dismissAll();
                            router.replace('/logout-success' as any);
                        }
                    } catch (error) {
                        logger.error('ProfileScreen:Logout failed due to technical/network error', error);
                        const message =
                            error instanceof Error ? error.message : t('profile.logoutError');
                        setToastMessage(t('profile.logoutError'));
                        setToastVisible(true);
                    }
                }}
                onCancel={() => {
                    setLogoutDialogVisible(false);
                }}
            />

            <Toast
                visible={toastVisible}
                message={toastMessage}
                onHide={() => setToastVisible(false)}
            />
        </ThemedView>
    );
}